import React from 'react'
import PropTypes from 'prop-types'
import { prop } from 'ramda'
import { useMakeSelector, useSelector } from 'hooks/redux'

import {
  getComponent,
  getDashboardType,
  getDataBookCodes,
  getLocationDefinition,
} from 'selectors/dash'

import PartSvg from 'components/SwitchAndGauge/Dash/Part'
import DraggableRef from 'components/SwitchAndGauge/Dash/Part/DraggableRef'
import { makeComponentMetaSelector } from 'selectors/navigator'
import { DASH_VIEW } from 'utils/constants'
import { getComponentImageFile } from 'components/SwitchAndGauge/utils'
import { getDependencyDimensions } from 'components/SwitchAndGauge/dependencyDimensions'
import CustomGraphicIcon from 'components/SwitchAndGauge/Dash/Part/CustomGraphicIcon'
import HardwiredSwitchIcon from 'components/SwitchAndGauge/Dash/Part/HardwiredSwitchIcon'
import { DASH_TYPE_Q_BUS_DATABOOK_CODE } from 'components/SwitchAndGauge/definitions/dashConstants'
import { PanelPartPositioner, PartIconsWrapper } from './PanelPartDetails.style'

/* eslint-disable react-hooks/exhaustive-deps */
// PBI 479416: keep the drawer dependency frame aligned with edited locations.
const Part = ({
  partOverride,
  dependency,
  isCustom,
  isHardwiredSwitch,
  isCustomHardWiredSwitch,
}) => {
  const { id: location } = useMakeSelector(makeComponentMetaSelector, {
    id: DASH_VIEW,
  })

  const component = useSelector(getComponent, location)
  const locationMeta = useSelector(getLocationDefinition, location)
  const dashboardType = useSelector(getDashboardType)
  const dataBookCodes = useSelector(getDataBookCodes)

  const { errorCode, locationDetail } = partOverride || component || {}

  // Check if the part has error and if it does not have location detail then that part cannot be dragable
  // but if part has location details selected then it can be dragable
  // this is basically rendering PartDetails UI to force parts to update can drop check
  const isPartDragable = !locationDetail && errorCode

  const Part = (
    <PanelPartPositioner>
      {isPartDragable ? (
        <PartSvg
          location={location}
          noTooltip
          noSelectedStyle
          disableOnClick
          disableEvents
          hidePendingPill
          panelNumber={partOverride?.panelNumber}
          detailView
          width={prop('width', locationMeta)}
          height={prop('height', locationMeta)}
        />
      ) : (
        <DraggableRef location={location}>
          <PartSvg
            location={location}
            noTooltip
            noSelectedStyle
            disableOnClick
            disableEvents
            hidePendingPill
            panelNumber={partOverride?.panelNumber}
            detailView
            width={prop('width', locationMeta)}
            height={prop('height', locationMeta)}
          />
        </DraggableRef>
      )}
      <PartIconsWrapper>
        {isCustomHardWiredSwitch ? (
          <>
            <CustomGraphicIcon />
            <HardwiredSwitchIcon />
          </>
        ) : (
          <>
            {isCustom && <CustomGraphicIcon />}
            {isHardwiredSwitch && <HardwiredSwitchIcon />}
          </>
        )}
      </PartIconsWrapper>
    </PanelPartPositioner>
  )
  const isDashTypeQBus =
    ['Q'].includes(dashboardType) &&
    dataBookCodes.includes(DASH_TYPE_Q_BUS_DATABOOK_CODE)

  const hideDependency =
    (isDashTypeQBus && ['AUPNL'].includes(locationMeta.panel)) || !dependency

  if (!hideDependency) {
    const PartDependency = getComponentImageFile(dependency?.panelNumber, true)
    const dependencyDimensions = getDependencyDimensions(
      dependency,
      locationMeta
    )

    return (
      <div
        style={{
          position: 'relative',
          ...dependencyDimensions,
        }}
      >
        <PartDependency
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            ...dependencyDimensions,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: dependency.top,
            left: dependency.left,
            zIndex: 10,
          }}
        >
          {Part}
        </div>
      </div>
    )
  }

  return Part
}

Part.propTypes = {
  partOverride: PropTypes.object,
  dependency: PropTypes.shape({
    panelNumber: PropTypes.string,
    top: PropTypes.number,
    left: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  isHardwiredSwitch: PropTypes.bool,
  isCustomHardWiredSwitch: PropTypes.bool,
  isCustom: PropTypes.bool,
}
export default Part
